package Structs.Api

import DataBase.UINKDBInterface
import Utils.DatesManipulator
import Utils.WrappedName

// Text-verify compare support, shared by the mobile Compose app and the legacy
// Android app. Purpose-built for drafts that came from pasted / WhatsApp text:
// the user's own message stays the anchor — every message line is shown
// verbatim with the parsed product row(s) directly underneath, so a supplier
// can scan top-to-bottom, confirm each line became the right product and
// quantity, and spot message lines that were NOT parsed.

/** One source-message line paired with the product rows parsed from it.
 *  `sourceLine == null` groups rows that have no matching message line
 *  (manually added lines). `products` empty = the line wasn't parsed. */
data class AiTextCompareRow(
    val sourceLine: String?,
    val products: List<AiResolvedProduct>,
)

/** Pair each non-blank line of the pasted message with the resolved rows whose
 *  `rawText` came from it (exact or containment match after whitespace
 *  normalization). Leftover products are appended as a no-source group. */
fun buildAiTextCompareRows(
    textInput: String?,
    products: List<AiResolvedProduct>,
): List<AiTextCompareRow> {
    val ws = Regex("\\s+")
    fun norm(s: String) = s.trim().replace(ws, " ")
    val lines = textInput.orEmpty().lines().map { it.trim() }.filter { it.isNotBlank() }
    val remaining = products.toMutableList()
    val rows = mutableListOf<AiTextCompareRow>()
    for (line in lines) {
        val n = norm(line)
        val matches = remaining.filter { p ->
            val raw = p.rawText?.let(::norm)
            !raw.isNullOrBlank() &&
                (raw == n || (raw.length >= 3 && (n.contains(raw) || raw.contains(n))))
        }
        remaining.removeAll(matches)
        rows += AiTextCompareRow(line, matches)
    }
    if (remaining.isNotEmpty()) rows += AiTextCompareRow(null, remaining.toList())
    return rows
}

/**
 * Layout decision for the text-verify list, shared by both phone apps.
 *
 * WhatsApp / pasted orders are short ("עגבניות 5", "פיתה 20") so on most phones
 * the message line, the matched product and the amount fit side by side — one
 * row per message line reads much faster than the stacked bubble + product
 * card. The decision is made ONCE for the whole list: a mix of one-line and
 * stacked rows is harder to scan than either alone, so if a single row would
 * overflow (long product name, a line that parsed into two products, a PDF-ish
 * line with barcodes) every row stacks.
 *
 * Widths are whatever unit the caller measures in (px or dp) as long as
 * [availableWidth] is the same unit and already has the fixed chrome removed
 * (icon, gaps, paddings). [measureUnparsed] is the width of the "not a product
 * line" note plus its manual-add button.
 */
fun aiTextCompareOneLine(
    rows: List<AiTextCompareRow>,
    availableWidth: Float,
    measureSource: (String) -> Float,
    measureProduct: (AiResolvedProduct) -> Float,
    measureAmount: (AiResolvedProduct) -> Float,
    measureUnparsed: () -> Float,
): Boolean {
    if (availableWidth <= 0f) return false
    val withSource = rows.filter { it.sourceLine != null }
    if (withSource.isEmpty()) return false
    var unparsedWidth = -1f
    for (row in withSource) {
        val source = row.sourceLine ?: continue
        // A line that split into several products has no one-line shape.
        if (row.products.size > 1) return false
        val product = row.products.firstOrNull()
        val need = if (product == null) {
            if (unparsedWidth < 0f) unparsedWidth = measureUnparsed()
            measureSource(source) + unparsedWidth
        } else {
            measureSource(source) + measureProduct(product) + measureAmount(product)
        }
        if (need > availableWidth) return false
    }
    return true
}

/** One-line compare row: what to print in the product slot. Unmatched lines show
 *  the fix prompt instead of echoing the message text as a "product". */
fun aiTextCompareProductLabel(p: AiResolvedProduct, processing: Boolean): String = when {
    p.productId == null && processing -> "ממתין להתאמה..."
    p.productId == null -> "ללא התאמה — הקש לתיקון"
    else -> (p.matchProductName ?: p.productName ?: "").ifEmpty { "—" }
}

/** ERP unit → its short label ("קרטון" → "קר'", "ק''ג" → "קג'"); unknown units
 *  pass through. Straight/Hebrew double quotes are normalised so `ק"ג` and
 *  `ק״ג` resolve like the catalog spelling `ק''ג`. */
fun aiUnitShort(unit: String?): String {
    val u = unit?.trim().orEmpty()
    if (u.isEmpty()) return ""
    val norm = u.replace("\"", "''").replace("״", "''").replace("׳", "'")
    val w = WrappedName.convertFrom(u) ?: WrappedName.convertFrom(norm)
    return w?.shortName()?.takeIf { it.isNotBlank() } ?: u
}

enum class AiCaptionTone { MUTED, OK, WARN, ERROR }

/** ERP dayLock letters (א=Sunday … ש=Saturday). */
val AI_HEBREW_WEEKDAYS = listOf("א", "ב", "ג", "ד", "ה", "ו", "ש")

const val AI_DAY_OFF_CAPTION = "⚠ לא זמין בניהול קו ליום זה"

fun aiProductDaysLabel(days: List<String>?): String? {
    val clean = days?.map { it.trim() }?.filter { it.isNotEmpty() } ?: return null
    return clean.takeIf { it.isNotEmpty() }?.joinToString(",")
}

fun aiHebrewWeekdayLetter(iso: String?): String? {
    val raw = iso?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    return runCatching {
        AI_HEBREW_WEEKDAYS.getOrNull(DatesManipulator.getDayOfWeekIsrael(raw))
    }.getOrNull()
}

/** null = no opinion (no days or no date). */
fun aiProductDayAvailable(days: List<String>?, date: String?): Boolean? {
    val clean = days?.map { it.trim() }?.filter { it.isNotEmpty() } ?: return null
    if (clean.isEmpty()) return null
    val letter = aiHebrewWeekdayLetter(date) ?: return null
    return letter in clean
}

fun aiEffectiveDayAvailable(
    days: List<String>?,
    date: String?,
    fallback: Boolean? = null,
): Boolean? = aiProductDayAvailable(days, date) ?: fallback

fun aiResolvedProductDays(p: AiResolvedProduct, catalogDays: List<String>? = null): List<String> {
    val fromMatch = p.matchDays.map { it.trim() }.filter { it.isNotEmpty() }
    if (fromMatch.isNotEmpty()) return fromMatch
    val fromCand = p.candidates.firstOrNull { it.id != null && it.id == p.productId }
        ?.days?.map { it.trim() }?.filter { it.isNotEmpty() }.orEmpty()
    if (fromCand.isNotEmpty()) return fromCand
    return catalogDays?.map { it.trim() }?.filter { it.isNotEmpty() }.orEmpty()
}

fun aiCatalogDaysForProduct(productId: Int?, supplier: Boolean): List<String> {
    if (productId == null) return emptyList()
    return runCatching {
        val db = UINKDBInterface.activeDB
        val product = if (supplier) db.getSupplierProduct(productId).first
        else db.getClientProduct(productId).first
        product?.getOrderProduct()?.daysString().orEmpty()
    }.getOrDefault(emptyList())
}

/** Stamp ERP weekday letters + recompute dayAvailable from the draft date. */
fun aiWithCatalogDays(
    p: AiResolvedProduct,
    docDate: String?,
    catalogDays: List<String>? = null,
): AiResolvedProduct {
    val days = aiResolvedProductDays(p, catalogDays)
    return p.copy(
        matchDays = days,
        matchDayAvailable = aiEffectiveDayAvailable(days, docDate, p.matchDayAvailable),
    )
}

data class AiCaptionPart(val text: String, val tone: AiCaptionTone)

/** The small meta line under a one-line compare row's product name: the id
 *  (company scheme, see [aiRowIdLabel]) and at most one status note. Kept
 *  terse on purpose — it must not cost vertical space. Unmatched lines get no
 *  status here because the product slot already prints the fix prompt. */
fun aiTextCompareCaption(
    p: AiResolvedProduct,
    idLabel: String?,
    processing: Boolean,
): List<AiCaptionPart> {
    val matched = p.productId != null
    val manual = matched && p.matchSource == "manual"
    val medium = matched && !manual && (p.matchConfidence ?: 1.0) < 0.85
    val out = mutableListOf<AiCaptionPart>()
    if (matched && !idLabel.isNullOrBlank()) out += AiCaptionPart("#$idLabel", AiCaptionTone.MUTED)
    when {
        manual -> out += AiCaptionPart("✓ שונה ידנית", AiCaptionTone.OK)
        processing && !matched -> out += AiCaptionPart("ממתין להתאמה...", AiCaptionTone.MUTED)
        !matched -> Unit
        p.matchAvailable == false -> out += AiCaptionPart("⚠ לא במלאי", AiCaptionTone.ERROR)
        p.matchDayAvailable == false -> out += AiCaptionPart(AI_DAY_OFF_CAPTION, AiCaptionTone.ERROR)
        medium -> {
            val pct = ((p.matchConfidence ?: 0.0) * 100.0 + 0.5).toInt().coerceIn(0, 100)
            out += AiCaptionPart("ביטחון $pct%", AiCaptionTone.WARN)
        }
    }
    return out
}

/** Identifier shown in small under a compare row's product name — the
 *  company's chosen scheme (web parity): external SKU (מק"ט) when the profile
 *  searches by external id (`idScope == "ext"`), else the internal ERP id.
 *  Unmatched lines fall back to the document's own SKU so the user can still
 *  find the product by it. [extIdById] is the local catalog's id→externalId
 *  map ("-1" = none). */
fun aiRowIdLabel(
    p: AiResolvedProduct,
    idScope: String,
    extIdById: Map<Int, String>,
): String? {
    fun valid(s: String?) = s?.takeIf { it.isNotBlank() && it != "-1" }
    val pid = p.productId
    if (idScope == "ext") {
        val ext = valid(pid?.let { extIdById[it] })
            ?: valid(p.candidates.firstOrNull { it.id != null && it.id == pid }?.extId)
            ?: valid(p.extId)
        if (ext != null) return ext
    }
    return pid?.toString()
}
